import {createAction, createAsyncThunk} from '@reduxjs/toolkit'

import {isApiError} from '@shared/helpers/api'
import {delayTimeout} from '@shared/helpers/function'
import {isEmpty, isEqual} from '@shared/helpers/lang'
import {ReceivePointResultCode, SchemasReceivePoint} from '@shared/schemas/payment'

import {OrderSheetPageSet, OrderSheetPageSetType} from '@web/core/constants'
import getApi, {createWrapper, getBaseUrl} from '@web/helpers/api'
import {CommonState, commonSelector, seqIDSelector} from '@web/slices/common/slice'
import {RewardState} from '@web/slices/reward/slice'

import api, {
    ExpectedRewardPointError,
    FamilyPayFetchExpectedPaymoneyRewardPointResponse,
    FetchExpectedMembershipPointParams,
    FetchExpectedRewardPointParams,
    GetReviewRewardInfoResponse,
} from './apis'
import {familyPayFailureRejectValue, ReceivePointCodeMap, ReceivePointActionResultCode} from './constant'

import type {
    BookingRewardResponse,
    ConfirmedBenefitCardRewardPointResponse,
    ConfirmedPurchaseRewardPointResponse,
    ConfirmedReviewRewardTotalPointResponse,
    EasyBookingFetchExpectedRewardPointResponse,
    ExpectedMembershipPointResponse,
    FetchBenefitAmountsV2Params,
    FetchBenefitAmountsV2Result,
    MallFetchExpectedRewardPointResponse,
    SellerFetchExpectedRewardPointResponse,
    ThunkApiConfig,
} from './apis'

/**
 * - 성공할때까지 count 횟수만큼 delay마다 api 조회 wrapper
 *   - count : 실패시 재조회 최대 횟수
 *   - delay : 재조회 간격 (ms)
 */
export async function asyncActionAutoRetryWrapper<T>(
    apiPromise: () => Promise<T>,
    {
        rejectWithValue,
        count,
        delay,
    }: {
        rejectWithValue: AnyFunction
        count: number
        delay: number
    },
) {
    let times = 0

    while (times < count) {
        const result = await apiPromise().catch(() => null)

        /** result가 0일때도 유효한 값으로 쓰이기 때문에, null 특정 지어서 체크 */
        if (result !== null) {
            return result
        }

        await delayTimeout(delay)

        times++
    }

    return rejectWithValue(null)
}

/** 적립 예상 포인트 재조회 */
export const fetchExpectedRewardPoint = createAsyncThunk<
    | {
          pageSet: typeof OrderSheetPageSet.MALL
          data: MallFetchExpectedRewardPointResponse
      }
    | {
          pageSet: typeof OrderSheetPageSet.EASYBOOKING
          data: EasyBookingFetchExpectedRewardPointResponse
      }
    | {
          pageSet: typeof OrderSheetPageSet.SELLER
          data: SellerFetchExpectedRewardPointResponse
      }
    | {
          pageSet: typeof OrderSheetPageSet.FAMILY_PAY
          data: FamilyPayFetchExpectedPaymoneyRewardPointResponse
      },
    {
        pageSet: Exclude<OrderSheetPageSetType, typeof OrderSheetPageSet.CLAIM>
        expectedRewardPointRequest: FetchExpectedRewardPointParams['expectedRewardPointRequest']
    },
    {
        state: {
            common: CommonState
        }
        rejectValue: ExpectedRewardPointError
    }
>(
    'rewardPoint/fetchExpectedRewardPoint',
    async ({pageSet, expectedRewardPointRequest}, {getState, rejectWithValue}) => {
        try {
            const state = getState()

            /** 패밀리결제 선택했는데 payerPayMemberNo가 없으면 reject */
            if (
                expectedRewardPointRequest?.selectedPayMethodTypes?.includes('FAMILY_PAY') &&
                !expectedRewardPointRequest.payerPayMemberNo
            ) {
                return rejectWithValue(familyPayFailureRejectValue)
            }

            const result = await api.fetchExpectedRewardPoint(pageSet, {
                orderSheetId: state.common.seqID,
                expectedRewardPointRequest,
            })
            if (pageSet === OrderSheetPageSet.MALL) {
                return {
                    pageSet,
                    data: result as MallFetchExpectedRewardPointResponse,
                }
            }

            if (pageSet === OrderSheetPageSet.EASYBOOKING) {
                return {
                    pageSet,
                    data: result as EasyBookingFetchExpectedRewardPointResponse,
                }
            }
            if (pageSet === OrderSheetPageSet.FAMILY_PAY) {
                return {
                    pageSet,
                    data: result as FamilyPayFetchExpectedPaymoneyRewardPointResponse,
                }
            }
            return {
                pageSet,
                data: result as SellerFetchExpectedRewardPointResponse,
            }
        } catch (e) {
            if (isApiError(e)) {
                return rejectWithValue(e.response?.data?.result)
            }
            throw e
        }
    },
)

let prevRequestParam: Maybe<FetchBenefitAmountsV2Params> = null
let prevRequestId = ''
/** 디지털 컨텐츠 서비스별 예상 적립금 조회 V2 */
export const getExpectedRewardPointV2 = createAsyncThunk<
    FetchBenefitAmountsV2Result,
    FetchBenefitAmountsV2Params,
    {state: {reward: RewardState; common: CommonState}; rejectValue: {code: string; message?: string}}
>('reward/getExpectedRewardPointV2', async (param, {getState, rejectWithValue, requestId}) => {
    const state = getState()
    const {pageSet} = commonSelector(state)

    if (isEmpty(param.requests)) {
        return rejectWithValue({code: 'EMPTY_ITEM', message: '적립금을 조회할 아이템 목록이 없습니다.'})
    }

    if (isEqual(prevRequestParam, param)) {
        return rejectWithValue({code: 'DUPLICATE_PARAM', message: '기존과 동일한 param을 제출했습니다.'})
    }

    /** 요청을 발생시키는 경우만 저장 */
    prevRequestId = requestId
    prevRequestParam = param

    try {
        const result = await api.fetchExpectedRewardPointV2(pageSet, param)

        if (prevRequestId !== requestId) {
            return rejectWithValue({
                code: 'OUTDATED_REQUEST',
                message: '최근 발생한 포인트 조회 요청 응답이 아닙니다.',
            })
        }

        return result
    } catch (e) {
        if (isApiError(e)) {
            /** API 실패시 > 동일 요청 검증 파라미터 초기화  */
            prevRequestParam = null
            prevRequestId = ''
            return rejectWithValue({code: 'API_ERROR'})
        }
        throw e
    }
})

/**
 * 주문완료 > 구매적립 확정 포인트 조회
 */
export const getConfirmedPurchaseRewardPoint = createAsyncThunk<
    ConfirmedPurchaseRewardPointResponse,
    {retryCount?: number; productOrderNos: string[]; familyPaymentOrderNo?: string; orderNo?: string},
    ThunkApiConfig
>(
    'reward/getConfirmedPurchaseRewardPoint',
    async ({productOrderNos, familyPaymentOrderNo, orderNo, retryCount}, {rejectWithValue}) => {
        if (isEmpty(productOrderNos)) {
            return rejectWithValue('productOrderNos이 유효하지 않습니다.')
        }

        return asyncActionAutoRetryWrapper(
            async function () {
                const result = await api.fetchConfirmedPurchaseRewardPoint({
                    productOrderNos,
                    familyPaymentOrderNo,
                    orderNo,
                })

                return result
            },
            {
                rejectWithValue,
                count: retryCount ?? 1,
                delay: 500,
            },
        )
    },
)

/**
 * 주문완료 > 리뷰적립 확정 포인트 합
 */
export const getConfirmedReviewRewardTotalPoint = createAsyncThunk<
    ConfirmedReviewRewardTotalPointResponse,
    {retryCount?: number} | undefined,
    ThunkApiConfig
>('reward/getConfirmedReviewRewardTotalPoint', async (autoRetryInfo, {getState, rejectWithValue}) => {
    const state = getState()
    const orderNo = seqIDSelector(state)

    if (!orderNo) {
        return rejectWithValue('orderNo이 유효하지 않습니다.')
    }

    return asyncActionAutoRetryWrapper(
        async function () {
            const result = await api.fetchConfirmedReviewRewardTotalPoint(orderNo)

            return result
        },
        {
            rejectWithValue,
            count: autoRetryInfo?.retryCount ?? 1,
            delay: 500,
        },
    )
})

/**
 * 주문완료 이후 > 리뷰적립 스냅샷(리뷰작성전) / 리뷰적립 이력(리뷰작성후) 정보 조회
 */
export const getReviewRewardInfo = createAsyncThunk<
    GetReviewRewardInfoResponse,
    {retryCount?: number; productOrderNos: string[]; writtenReviewProductOrderNos?: string[]},
    ThunkApiConfig
>(
    'reward/getReviewRewardInfo',
    async ({productOrderNos, retryCount, writtenReviewProductOrderNos}, {rejectWithValue}) => {
        if (isEmpty(productOrderNos) && isEmpty(writtenReviewProductOrderNos)) {
            return rejectWithValue('productOrderNos이 유효하지 않습니다.')
        }

        return asyncActionAutoRetryWrapper(
            async function () {
                const result = await api.fetchReviewRewardInfo({
                    productOrderNos,
                    writtenReviewProductOrderNos,
                })

                return result
            },
            {
                rejectWithValue,
                count: retryCount ?? 1,
                delay: 500,
            },
        )
    },
)

/**
 * 주문완료 > 혜택카드 확정 포인트
 */
export const getConfirmedBenefitCardRewardPoint = createAsyncThunk<
    ConfirmedBenefitCardRewardPointResponse,
    {retryCount?: number; tid: string},
    ThunkApiConfig
>('reward/getConfirmedBenefitCardRewardPoint', async ({tid, retryCount}, {rejectWithValue}) => {
    if (!tid) {
        return rejectWithValue('tid이 유효하지 않습니다.')
    }

    return asyncActionAutoRetryWrapper(
        async function () {
            const result = await api.fetchConfirmedBenefitCardRewardTotalPoint(tid)

            return result
        },
        {
            rejectWithValue,
            count: retryCount ?? 1,
            delay: 500,
        },
    )
})

/** 멤버십 예상 적립금 조회 */
export const getExpectedMembershipPoint = createAsyncThunk<
    ExpectedMembershipPointResponse,
    {retryCount?: number; productsInfo: FetchExpectedMembershipPointParams; productOrderNos: string[]},
    ThunkApiConfig
>('reward/getExpectedMembershipPoint', async ({productsInfo, productOrderNos, retryCount}, {rejectWithValue}) => {
    return asyncActionAutoRetryWrapper(
        async function () {
            const result = await api.fetchExpectedMembershipPoint(productsInfo)

            /** 멤버십 예상 적립 API > 요청한 상품 순서대로 응답이 내려오고, 해당 응답에 unique key값이 없으므로 구분을 위해 가공하여 주입 */
            return {
                ...result,
                products: result.products.map((product, index) => {
                    return {
                        ...product,
                        productOrderNo: productOrderNos[index],
                    }
                }),
            }
        },
        {
            rejectWithValue,
            count: retryCount ?? 1,
            delay: 500,
        },
    )
})

/**
 * 네이버예약 적립 조회
 */
export const getBookingReward = createAsyncThunk<
    BookingRewardResponse,
    {retryCount?: number; bookingIds: string},
    ThunkApiConfig
>('reward/getBookingReward', async ({bookingIds, retryCount}, {rejectWithValue}) => {
    if (isEmpty(bookingIds)) {
        return rejectWithValue('bookingIds 유효하지 않습니다.')
    }

    return asyncActionAutoRetryWrapper(
        async function () {
            const result = await api.fetchEasyBookingRewardPoint({bookingIds})

            return result
        },
        {
            rejectWithValue,
            count: retryCount ?? 1,
            delay: 500,
        },
    )
})

export const waitRewardApi = createAction('reward/waitRewardApi')

/** 온라인 결제 > 포인트 받기 */
export const receivePoint = createAsyncThunk<
    SchemasReceivePoint['/:paymentId']['response'],
    SchemasReceivePoint['/:paymentId']['request'],
    ThunkApiConfig
>('reward/receivePoint', async ({paymentId}, {rejectWithValue}) => {
    try {
        const result = await createWrapper<SchemasReceivePoint['/:paymentId']['response']>(
            getApi(getBaseUrl()).post(`/payment/receivePoint/${paymentId}`),
        )

        if (result.resultCode !== ReceivePointResultCode.SUCCESS) {
            return rejectWithValue({
                code: ReceivePointCodeMap[result.resultCode] ?? ReceivePointActionResultCode.FAILED,
            })
        }

        return result
    } catch {
        return rejectWithValue({
            code: ReceivePointActionResultCode.FAILED,
        })
    }
})

/** 온라인 결제 > 추가 적립 가능한 주문건 조회 */
export const getAdditionalReward = createAsyncThunk<
    SchemasReceivePoint['/additionalReward']['response'],
    SchemasReceivePoint['/additionalReward']['request'],
    ThunkApiConfig
>('reward/getAdditionalReward', async (params, {rejectWithValue}) => {
    try {
        const result = await createWrapper<SchemasReceivePoint['/additionalReward']['response']>(
            getApi(getBaseUrl()).get('/payment/receivePoint/additionalReward', {params}),
        )

        return result
    } catch (error) {
        return rejectWithValue(error)
    }
})

/** 온라인 결제 > 적립에 대한 요약 정보 조회 */
export const getRewardSummary = createAsyncThunk<
    SchemasReceivePoint['/rewardSummary']['response'],
    SchemasReceivePoint['/rewardSummary']['request'],
    ThunkApiConfig
>('reward/getRewardSummary', async (params) => {
    const result = await createWrapper<SchemasReceivePoint['/rewardSummary']['response']>(
        getApi(getBaseUrl()).get('/payment/receivePoint/rewardSummary', {params}),
    )

    return result
})
